import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsArticle } from "@/mocks/news";
import { formatDate } from "@/utils/news";

export default function HeroCarousel({ items }: { items: NewsArticle[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => setIndex((v) => (v + 1) % items.length), 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  const go = (dir: number) => setIndex((v) => (v + dir + items.length) % items.length);

  return (
    <div className="relative w-full rounded-lg overflow-hidden bg-secondary-900 h-[300px] md:h-[440px] group">
      <Link to={`/detail/${active.id}`} className="block w-full h-full cursor-pointer">
        <img
          key={active.id}
          src={active.cover}
          alt={active.title}
          title={`${active.title} - 吃瓜爆料网`}
          className="w-full h-full object-cover object-top animate-fade-in"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/90 via-secondary-950/40 to-transparent" />
        <div className="absolute left-0 right-0 bottom-0 p-5 md:p-7">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
              焦点
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-100 text-xs backdrop-blur">
              {active.category}
            </span>
          </div>
          <h1 className="text-xl md:text-3xl font-bold text-background-50 leading-snug max-w-3xl line-clamp-2">
            {active.title}
          </h1>
          <p className="mt-2 text-sm md:text-base text-background-200 leading-relaxed max-w-2xl line-clamp-2 hidden sm:block">
            {active.summary}
          </p>
          <p className="mt-3 text-xs text-background-300 flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {formatDate(active.publishTime)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {active.author}
            </span>
          </p>
        </div>
      </Link>

      <button
        type="button"
        aria-label="上一条"
        onClick={(e) => {
          e.preventDefault();
          go(-1);
        }}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 text-xl backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
      >
        <i className="ri-arrow-left-s-line" />
      </button>
      <button
        type="button"
        aria-label="下一条"
        onClick={(e) => {
          e.preventDefault();
          go(1);
        }}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 text-xl backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
      >
        <i className="ri-arrow-right-s-line" />
      </button>

      <div className="absolute right-4 bottom-5 flex items-center gap-1.5">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 条`}
            onClick={(e) => {
              e.preventDefault();
              setIndex(i);
            }}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}